import React, { useState, useEffect, useRef } from 'react';
import './Login.css';
import CodeVerification from './CodeVerification';
import { sendLoginRequest, sendAuthorizationRequest } from '../utils/httpApi';
import { loginWithPasskey, passkeysSupported } from '../utils/webauthn';
import { consumeLogoutNotice } from '../utils/auth';
import config from '../config';

function Login({ onLogin, userEmail, onShowRegister, onShowPrivacy }) {
  const [email, setEmail] = useState(userEmail || '');
  const [error, setError] = useState('');
  const [showCodeVerification, setShowCodeVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);

  // If we got here because the session was force-ended (a failed token refresh
  // replaces the page with '/'), say so. Otherwise that path is silent and the
  // user just sees the login screen again with no explanation.
  useEffect(() => {
    const notice = consumeLogoutNotice();
    if (notice) setError(notice);
  }, []);

  // Initialize Turnstile widget when component mounts
  useEffect(() => {
    if (!config.TURNSTILE_ENABLED) {
      return;
    }

    // Wait for Turnstile to be loaded
    const initTurnstile = () => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: config.TURNSTILE_SITE_KEY,
            callback: (token) => {
              setCaptchaToken(token);
            },
            'error-callback': (errorCode) => {
              console.error('❌ Turnstile error:', errorCode);
              setCaptchaToken('');
              // Self-heal: reset the widget after a beat instead of telling the
              // user to refresh the page — transient network blips are common.
              setTimeout(() => {
                try { window.turnstile && window.turnstile.reset(widgetIdRef.current); } catch (_) { /* ignore */ }
              }, 1500);
            },
            'expired-callback': () => {
              // Token lifetime is ~5 min; in managed mode the solved widget is
              // collapsed/hidden, so without an explicit reset the user would be
              // stuck with no token AND no visible widget to re-solve — the
              // "verification disappeared then login errored" bug.
              setCaptchaToken('');
              try { window.turnstile && window.turnstile.reset(widgetIdRef.current); } catch (_) { /* ignore */ }
            }
          });
        } catch (err) {
          console.error('Failed to render Turnstile:', err);
        }
      }
    };

    // Try to initialize immediately
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for script to load
      const timer = setInterval(() => {
        if (window.turnstile) {
          initTurnstile();
          clearInterval(timer);
        }
      }, 100);

      return () => clearInterval(timer);
    }

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.error('Failed to remove Turnstile:', err);
        }
      }
    };
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // CAPTCHA validation (only if enabled). If React state missed the token
    // (callback timing / component remount), salvage it from the hidden input
    // Turnstile writes into its container — the widget can have solved
    // successfully (and collapsed itself) without our state knowing.
    let effectiveCaptchaToken = captchaToken;
    if (config.TURNSTILE_ENABLED && !effectiveCaptchaToken) {
      const domToken = containerRef.current
        ?.querySelector('input[name="cf-turnstile-response"]')?.value;
      if (domToken) {
        effectiveCaptchaToken = domToken;
        setCaptchaToken(domToken);
      }
    }
    if (config.TURNSTILE_ENABLED && !effectiveCaptchaToken) {
      // No token at all — re-arm the widget so the user can solve it, instead
      // of dead-ending with a collapsed invisible widget.
      try { window.turnstile && window.turnstile.reset(widgetIdRef.current); } catch (_) { /* ignore */ }
      setError('Please complete the security verification');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Send LOGIN request to HTTP API with captcha token
      const response = await sendLoginRequest(email, effectiveCaptchaToken);

      // Check if login was successful - loginResult must be VALIDATION_CODE_SENT
      // Note: header.apiStatus can be 'OK' even when login fails, so we must check loginResult first
      if (response.loginResult === 'FAILURE' || response.header?.errors?.length > 0) {
        // Handle explicit failure
        const errorMessage = response.message || response.header?.errors?.[0]?.message || 'Login failed. Please try again.';
        setError(errorMessage);

        // Reset CAPTCHA on error
        if (config.TURNSTILE_ENABLED && widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setCaptchaToken('');
        }
      } else if (response.loginResult === 'VALIDATION_CODE_SENT') {
        // Show code verification step only on explicit success
        setShowCodeVerification(true);
      } else {
        // Unknown response - treat as error
        const errorMessage = response.message || 'Unexpected response from server. Please try again.';
        setError(errorMessage);

        // Reset CAPTCHA on error
        if (config.TURNSTILE_ENABLED && widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setCaptchaToken('');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to connect to server. Please try again.');

      // Reset CAPTCHA on error
      if (config.TURNSTILE_ENABLED && widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setCaptchaToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerify = async (code) => {
    try {
      // Format code as ABC-DEF (add dash after 3rd character)
      const formattedCode = code.length === 6 ? `${code.substring(0, 3)}-${code.substring(3)}` : code;

      // Send VALIDATE request to HTTP API
      const response = await sendAuthorizationRequest(email, formattedCode);

      // Check if validation was successful (per WebSocket guide format)
      if ((response.header?.apiStatus === 'OK' || response.message === 'Validation successful') && response.accessToken) {
        // Pass successful auth to parent
        onLogin(response);
      } else {
        // Return error to code verification component
        const errorMessage = response.message || response.header?.errors?.[0]?.message || 'Invalid code. Please try again.';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Validation error:', err);
      throw err; // Re-throw to let CodeVerification handle it
    }
  };

  // Passkey sign-in: no captcha, no email code. With an email typed we scope
  // to that account's credentials; empty email uses the resident-key flow
  // (the platform's own passkey picker - the usual Face ID / fingerprint
  // path on mobile). Success returns the same authorise-shaped response as
  // the code flow, so onLogin handles both identically.
  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const response = await loginWithPasskey(email && email.trim() ? email.trim() : null);
      onLogin(response);
    } catch (err) {
      console.error('Passkey login error:', err);
      // NotAllowedError used to be swallowed as "user dismissed the prompt".
      // The spec makes it deliberately ambiguous for privacy, so platforms also
      // throw it for a timeout, an rpId mismatch and other internal failures —
      // including AFTER the biometric check has already succeeded. Swallowing it
      // meant a real failure was indistinguishable from a cancel: the user
      // passed Face ID and landed back on an unchanged login screen with
      // nothing on it. Always say something.
      if (err && err.name === 'NotAllowedError') {
        setError('Passkey sign-in did not complete. Try again, or use the email code.');
      } else {
        setError(err.message || 'Passkey sign-in failed. Try the email code instead.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleBack = () => {
    setShowCodeVerification(false);
    setError('');
  };

  // Show code verification screen if email has been submitted
  if (showCodeVerification) {
    return (
      <CodeVerification
        email={email}
        onVerify={handleCodeVerify}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <img src="/aviotrack_logo.png" alt="AvioTrack" />
        </div>
        <p className="login-subtitle">Aircraft Tracking System</p>

        <form onSubmit={handleEmailSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="email-input"
              autoFocus
            />
          </div>

          {/* Turnstile CAPTCHA Widget */}
          {config.TURNSTILE_ENABLED && (
            <div className="form-group captcha-container">
              <div ref={containerRef} id="turnstile-login-widget"></div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Sending...' : 'Login'}
          </button>

          {passkeysSupported() && (
            <button
              type="button"
              className="login-button passkey-button"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading}
            >
              {passkeyLoading ? 'Waiting for passkey...' : 'Sign in with a passkey'}
            </button>
          )}

          {onShowRegister && (
            <div className="register-link-container">
              <p className="register-text">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={onShowRegister}
                  className="register-link"
                >
                  Register here
                </button>
              </p>
            </div>
          )}
        </form>
        <div className="login-footer">
          <button type="button" onClick={onShowPrivacy} className="login-footer-link">Privacy Policy</button>
        </div>
      </div>
    </div>
  );
}

export default Login;
